import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import Cookies from "js-cookie";

const TranslationContext = createContext();
const DEFAULT_LANGUAGE = "en";
const LANGUAGE_COOKIE = "preferred_language";
const TRANSLATIONS_STORAGE_PREFIX = "translations:";
const FALLBACK_TRANSLATIONS = {
  en: {
    AnErrorOccurredWhileSendingTheCode: "An error occurred while sending the code.",
    CheckYourEmail: "Check your email",
    CheckYourWhatsApp: "Check your WhatsApp",
    DidntReceiveCode: "Didn't receive the code?",
    FailedToSendVerificationCode: "Unable to send a new verification code.",
    InvalidVerificationCode: "The verification code is incorrect.",
    LastName: "Last Name",
    Loading: "Loading",
    OtherServices: "Other Services",
    Package: "Package",
    Packages: "Packages",
    PaymentFee: "Payment Fee",
    PleaseEnterCode: "Enter the 6-digit verification code.",
    RegisterLastName: "Last Name",
    RegisterMarketingConsent: "I agree to receive advertisements",
    RegisterTermsConsent: "I agree to the terms and conditions",
    RegisterTermsRequired: "You must agree to the terms and conditions",
    ResendCode: "Resend code",
    Services: "Other Services",
    VerificationCode: "Verification code",
    VerificationCodeHint: "Enter the six digits we sent you.",
    VerificationCodeSentSuccessfully: "A new verification code has been sent.",
    VerificationFailed: "We could not verify the code. Please try again.",
    VerificationSessionExpired: "This verification session has expired. Please request a new code.",
    VerifyCode: "Verify code",
  },
  ar: {
    AnErrorOccurredWhileSendingTheCode: "حدث خطأ أثناء إرسال الرمز.",
    CheckYourEmail: "تحقق من بريدك الإلكتروني",
    CheckYourWhatsApp: "تحقق من واتساب",
    DidntReceiveCode: "لم يصلك الرمز؟",
    FailedToSendVerificationCode: "تعذر إرسال رمز تحقق جديد.",
    InvalidVerificationCode: "رمز التحقق غير صحيح.",
    LastName: "الاسم الاخير",
    Loading: "جارٍ التحميل",
    Flights: "الطيران",
    Hotels: "الاقامة",
    OtherServices: "خدمات أخرى",
    Package: "البرامج السياحية",
    Packages: "البرامج السياحية",
    PaymentFee: "رسوم الدفع",
    PleaseEnterCode: "أدخل رمز التحقق المكوّن من 6 أرقام.",
    RegisterLastName: "الاسم الاخير",
    RegisterMarketingConsent: "اوافق على تلقي الاعلانات",
    RegisterTermsConsent: "اوافق على الشروط والأحكام",
    RegisterTermsRequired: "يجب الموافقة على الشروط والأحكام",
    ResendCode: "إعادة إرسال الرمز",
    Services: "خدمات أخرى",
    VerificationCode: "رمز التحقق",
    VerificationCodeHint: "أدخل الأرقام الستة التي أرسلناها لك.",
    VerificationCodeSentSuccessfully: "تم إرسال رمز تحقق جديد.",
    VerificationFailed: "تعذر التحقق من الرمز. حاول مرة أخرى.",
    VerificationSessionExpired: "انتهت جلسة التحقق. اطلب رمزاً جديداً.",
    VerifyCode: "تأكيد الرمز",
  },
};
const TRANSLATION_OVERRIDES = {
  ar: {
    LastName: "الاسم الاخير",
    Flights: "الطيران",
    Hotels: "الاقامة",
    Package: "البرامج السياحية",
    Packages: "البرامج السياحية",
    Services: "خدمات أخرى",
  },
};
const TRANSLATION_VALUE_OVERRIDES = {
  ar: {
    "الرحلات الجوية": "الطيران",
    "الرحلة": "البرامج السياحية",
    "الخدمات": "خدمات أخرى",
    "الفنادق": "الاقامة",
  },
};

const applyTranslationValueOverrides = (value, language) => {
  if (typeof value !== "string") {
    return value;
  }

  const replacements = TRANSLATION_VALUE_OVERRIDES[language];
  if (!replacements) {
    return value;
  }

  return Object.entries(replacements).reduce(
    (nextValue, [searchValue, replacementValue]) =>
      nextValue.replaceAll(searchValue, replacementValue),
    value
  );
};

const resolveLanguage = (lang) => (lang === "ar" ? "ar" : DEFAULT_LANGUAGE);

const getTranslationsStorageKey = (lang) =>
  `${TRANSLATIONS_STORAGE_PREFIX}${resolveLanguage(lang)}`;

const persistLanguageCookie = (lang) => {
  const resolvedLanguage = resolveLanguage(lang);
  Cookies.remove(LANGUAGE_COOKIE);
  Cookies.remove(LANGUAGE_COOKIE, { path: "/" });
  Cookies.set(LANGUAGE_COOKIE, resolvedLanguage, { path: "/", expires: 365 });
};

const applyDocumentLanguage = (lang) => {
  const resolvedLanguage = resolveLanguage(lang);
  document.documentElement.setAttribute("lang", resolvedLanguage);
  document.documentElement.setAttribute(
    "dir",
    resolvedLanguage === "ar" ? "rtl" : "ltr"
  );
};

const getStoredTranslations = (lang) => {
  const storageKey = getTranslationsStorageKey(lang);
  const storedTranslations = localStorage.getItem(storageKey);

  if (!storedTranslations) {
    return {};
  }

  try {
    return JSON.parse(storedTranslations);
  } catch (error) {
    console.error(`Error reading cached translations: ${error}`);
    localStorage.removeItem(storageKey);
    return {};
  }
};

const hasTranslations = (value) => Object.keys(value).length > 0;

export const TranslationProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() =>
    resolveLanguage(Cookies.get(LANGUAGE_COOKIE))
  );
  const [translations, setTranslations] = useState(() =>
    getStoredTranslations(resolveLanguage(Cookies.get(LANGUAGE_COOKIE)))
  );

  const fetchTranslations = useCallback(async (lang = DEFAULT_LANGUAGE) => {
    const requestedLanguage = resolveLanguage(lang);

    try {
      const response = await fetch(
        `https://alltimetest7.inter-netsys.com/api/method/alltimes.www.api.get_translation_file?lang=${requestedLanguage}`,
        {
          method: "GET",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!response.ok) {
        console.error(`HTTP error! status: ${response.status}`);
        return;
      }

      const data = await response.json();
      const nextTranslations = data.message || {};

      setTranslations(nextTranslations);
      localStorage.setItem(
        getTranslationsStorageKey(requestedLanguage),
        JSON.stringify(nextTranslations)
      );
      return nextTranslations;
    } catch (error) {
      console.error(`Error fetching translations: ${error}`);
      return null;
    }
  }, []);

  const setLanguage = useCallback((lang) => {
    const resolvedLanguage = resolveLanguage(lang);
    const cachedTranslations = getStoredTranslations(resolvedLanguage);

    persistLanguageCookie(resolvedLanguage);
    applyDocumentLanguage(resolvedLanguage);
    setCurrentLanguage(resolvedLanguage);

    if (hasTranslations(cachedTranslations)) {
      setTranslations(cachedTranslations);
    }
  }, []);


// const fetchTranslations = async (lang) => {
//   try {
//     const response = await axiosPost(
//       '/api/method/alltimes.www.api.get_translation_file',
//       { lang }, // sending lang in POST body
//       { "Content-Type": "application/json" }
//     );

//     if (response.status !== 200) {
//       console.error(`HTTP error! status: ${response.status}`);
//       return;
//     }

//     const data = response.data;
//     setTranslations(data.message);
//     localStorage.setItem("translations", JSON.stringify(data.message));
//   } catch (error) {
//     console.error(`Error fetching translations: ${error}`);
//   }
// };

  const t = (key) => {
    const translatedValue =
      TRANSLATION_OVERRIDES[currentLanguage]?.[key] ||
      translations[key] ||
      FALLBACK_TRANSLATIONS[currentLanguage]?.[key] ||
      FALLBACK_TRANSLATIONS[DEFAULT_LANGUAGE]?.[key] ||
      key;

    return applyTranslationValueOverrides(translatedValue, currentLanguage);
  };

  useEffect(() => {
    localStorage.removeItem("translations");
  }, []);

  useEffect(() => {
    persistLanguageCookie(currentLanguage);
    applyDocumentLanguage(currentLanguage);

    const cachedTranslations = getStoredTranslations(currentLanguage);
    if (hasTranslations(cachedTranslations)) {
      setTranslations(cachedTranslations);
    }

    fetchTranslations(currentLanguage);
  }, [currentLanguage, fetchTranslations]);

  return (
    <TranslationContext.Provider
      value={{ t, fetchTranslations, currentLanguage, setLanguage }}
    >
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = () => useContext(TranslationContext);
